import React, { useState } from 'react'
import PropTypes from 'prop-types'
import gql from 'graphql-tag'
import { useQuery } from 'react-apollo'

import AchievementsLiveLessons from 'src/pages/AchievementsLiveLessons/AchievementsLiveLessons'
import DataFetchError, {
  ERROR_CODE_500,
} from 'src/ui/DataFetchError/DataFetchError'
import AchievementsLiveLessonsPlaceholder from 'src/pages/AchievementsLiveLessons/AchievementsLiveLessonsPlaceholder'

export const FETCH_COMPLETED_APPOINTMENTS = gql`
  query FETCH_COMPLETED_APPOINTMENTS(
    $l2Code: LanguageEnum!
    $first: Int
    $afterCursor: String
  ) {
    currentUser {
      login
      progress(code: $l2Code) {
        stats(
          period: total
          kinds: [
            live_lesson_total_lessons
            live_lesson_all_existing_exercises
            live_lesson_total_exercises
            live_lesson_exercises_feeling_good
            live_lesson_total_hours
          ]
        ) {
          count
          kind
        }
      }
      completedAppointments(code: $l2Code, first: $first, after: $afterCursor) {
        pageInfo {
          pageAfterCursors
        }
        edges {
          cursor
          node {
            averageStudentRating
            chatSessionArchiveUrl
            id
            liveLessonNumber
            chatSessionId
            startTimeAgo
            duration
            ratingAgainForTutor
            exercises(last: 1) {
              nodes {
                imageUrl
              }
            }
            unit {
              name
              cefrLevel
            }
            tutor {
              login
              avatarUrl
              name
              tutor {
                isFavorite
              }
            }
          }
        }
      }
    }
  }
`

const AchievementsLiveLessonsLazy = ({ l2Code }) => {
  const [page, setPage] = useState(1)
  const [hasLoaded, setHasLoaded] = useState(true)

  const limit = 10
  const progressData = useQuery(FETCH_COMPLETED_APPOINTMENTS, {
    variables: {
      l2Code,
      first: limit,
    },
  })
  const { data, loading, fetchMore } = progressData

  if (loading) {
    return <AchievementsLiveLessonsPlaceholder />
  }

  const stats = data?.currentUser?.progress?.stats
  const filterBy = (kind) => (stat) => stat?.kind == kind
  const allTimeStatsProps = {
    hoursCount: stats?.find(filterBy('live_lesson_total_hours'))?.count || 0,
    liveLessonCount:
      stats?.find(filterBy('live_lesson_total_lessons'))?.count || 0,
    exerciseTakenCount:
      stats?.find(filterBy('live_lesson_total_exercises'))?.count || 0,
    exerciseGoodCount:
      stats?.find(filterBy('live_lesson_exercises_feeling_good'))?.count || 0,
    allExercisesCount:
      stats?.find(filterBy('live_lesson_all_existing_exercises'))?.count || 0,
  }

  const liveLessonsData = data?.currentUser?.completedAppointments?.edges?.map(
    (e) => e.node
  )
  const pageInfo = data?.currentUser?.completedAppointments?.pageInfo

  const numOfSteps = pageInfo?.pageAfterCursors?.length

  const handleSetPage = async (newPage) => {
    if (!hasLoaded || newPage < 1 || newPage > numOfSteps) {
      return
    }

    setPage(newPage)

    const variables = {
      l2Code,
      first: limit,
      afterCursor: pageInfo.pageAfterCursors[newPage - 1],
    }

    // dont allow page changes while waiting for new page
    setHasLoaded(false)
    await fetchMore({
      variables,
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev
        return fetchMoreResult
      },
    })
    setHasLoaded(true)

    return
  }
  const paginationProps = {
    numOfSteps,
    currentStep: page,
    onFinish: null,
    onChangeStep: handleSetPage,
    // graphql pageInfo hasNextPage not always working, so calculate manually instead
    hasNextPage: page < numOfSteps,
  }

  if (progressData && data) {
    return (
      <AchievementsLiveLessons
        statsProps={allTimeStatsProps}
        paginationProps={paginationProps}
        liveLessonsData={liveLessonsData}
      />
    )
  }
  return <DataFetchError errorCode={ERROR_CODE_500} />
}

AchievementsLiveLessonsLazy.propTypes = {
  l2Code: PropTypes.string.isRequired,
}

export default AchievementsLiveLessonsLazy
